// Footer actions: export the report element to a multi-page PDF, and share the
// current report URL via a small menu (copy link, X, WhatsApp, Telegram,
// LinkedIn, email, and the native share sheet where available).

import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// External share intents. Each `href(url, title)` returns a link to open in a
// new tab; these are plain URLs, so no third-party script is loaded.
const SHARE_TARGETS = [
  {
    key: "x",
    label: "X",
    href: (url, title) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    href: (url, title) => `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
  },
  {
    key: "telegram",
    label: "Telegram",
    href: (url, title) =>
      `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    href: (url) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
  {
    key: "email",
    label: "Email",
    href: (url, title) =>
      `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${title}\n\n${url}`)}`,
  },
];

/**
 * @param {object} props
 * @param {() => HTMLElement|null} props.getElement Returns the node to export.
 * @param {string} props.fileName PDF file name (without extension).
 * @param {string} props.shareTitle Human title used in share text.
 * @param {Record<string, string>} [props.shareParams] Extra query params to add
 *   to the shared URL so a cold open still has enough context.
 */
export default function ReportActions({ getElement, fileName, shareTitle, shareParams }) {
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const shareUrl = () => {
    const url = new URL(window.location.href);
    Object.entries(shareParams || {}).forEach(([key, value]) => {
      if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
    });
    return url.toString();
  };

  const title = shareTitle || fileName;

  const handleDownload = async () => {
    const node = getElement();
    if (!node || busy) return;
    setBusy(true);
    try {
      // scale: 1 + quality 0.75 overshot — ~900 KB with visibly softer text
      // and maps. scale: 1.5 keeps most of that pixel-count saving (2.25x vs
      // scale 2's 4x) while noticeably sharper than scale: 1, and JPEG 0.92
      // is a high-quality setting with compression only kicking in on the
      // photographic map tiles, not the flat chart/text areas.
      const canvas = await html2canvas(node, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        ignoreElements: (el) =>
          el.classList?.contains("maplibregl-control-container") ||
          el.classList?.contains("maplibregl-ctrl"),
      });
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      const imgData = canvas.toDataURL("image/jpeg", 0.92);

      pdf.addImage(imgData, "JPEG", 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, pageWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`${fileName}.pdf`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Report PDF export failed — falling back to print", err);
      window.print();
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      /* clipboard blocked — ignore */
    }
    setMenuOpen(false);
  };

  const handleNativeShare = async () => {
    setMenuOpen(false);
    try {
      await navigator.share({ title, url: shareUrl() });
    } catch (err) {
      /* dismissed */
    }
  };

  const openTarget = (target) => {
    const href = target.href(shareUrl(), title);
    if (target.key === "email") {
      window.location.href = href;
    } else {
      window.open(href, "_blank", "noopener,noreferrer");
    }
    setMenuOpen(false);
  };

  return (
    <div className="report-actions hide-on-print">
      <button type="button" className="report-actions__primary" onClick={handleDownload} disabled={busy}>
        {busy ? "Preparing…" : "Download full report (PDF)"}
      </button>

      <div className="report-share" ref={wrapRef}>
        <button
          type="button"
          className="report-actions__secondary"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {copied ? "Link copied" : "Share report"}
        </button>

        {menuOpen && (
          <div className="report-share__menu" role="menu">
            <button type="button" role="menuitem" onClick={handleCopy}>
              Copy link
            </button>
            {SHARE_TARGETS.map((t) => (
              <button key={t.key} type="button" role="menuitem" onClick={() => openTarget(t)}>
                {t.label}
              </button>
            ))}
            {typeof navigator !== "undefined" && navigator.share && (
              <button type="button" role="menuitem" onClick={handleNativeShare}>
                More…
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
